import React, { Fragment, useState, useEffect } from 'react';
import { withServerData, useFetchJson } from '@xingternal/hops';

import ReactNotification from 'react-notifications-component';
import 'react-notifications-component/dist/theme.css?global';

import { GlobalStyles } from '@xingternal/tokens';
import GlobalStylesLegacy from '@xingternal/global-styles';
import { XingFrameProvider } from '@xingternal/xing-frame-provider';
import withApolloData from '@xingternal/xing-frame-provider/withApolloData';
import withStaticData from '@xingternal/xing-frame-provider/withStaticData';
import navigationData from '@xingternal/xing-frame-provider/data/navigation';
import XingView from '@xingternal/xing-view';
import { LoggedoutView } from '@xingternal/platform-layout-logged-out';
import ContainedGrid from '@xingternal/xing-grid';

import { useHistory } from 'react-router-dom';
import { Headline } from '@xingternal/typography';

import styles from './Frame.css';
import newWorkLogo from './images/new-work.svg';
import { User } from './models/user';
import { CurrentUserResponse } from './api/responses/api-response-types';
import { ApiPathConfig } from './api/api-path-config';
import { RoutesConfig } from './RoutesConfig';
import { isWebview } from './utils';

const LoggedInFrameProvider = withApolloData(XingFrameProvider);
const StaticFrame = withStaticData({ navigationData })(XingFrameProvider);

const frameProps = {
  flavour: 'xdl',
  userConsentSettings: { disableBanner: true },
};

const PublicView = ({
  authenticated,
  children,
}: {
  authenticated: boolean;
  children: React.ReactElement;
}) => {
  // Hide header when rendered inside webviews
  if (isWebview((global as any).navigator)) {
    return (
      <ContainedGrid type="standard" className={styles.spacedContainer}>
        {children}
      </ContainedGrid>
    );
  }

  if (authenticated) {
    return <XingView>{children}</XingView>;
  } else {
    return <LoggedoutView>{children}</LoggedoutView>;
  }
};

export const PublicAppFrame = () =>
  // Since React 18, the withServerData hoc is broken because children are not included in the type anymore
  // This is an oversight from the hops team
  // eslint-disable-next-line
  // @ts-ignore
  withServerData(({ children, serverData: { authenticated } }) => {
    if (authenticated) {
      return (
        <LoggedInFrameProvider {...frameProps}>
          <GlobalStylesLegacy>
            <GlobalStyles />
            <PublicView authenticated={authenticated}>
              <Fragment>{children}</Fragment>
            </PublicView>
          </GlobalStylesLegacy>
        </LoggedInFrameProvider>
      );
    }
    return (
      <StaticFrame {...frameProps}>
        <GlobalStylesLegacy>
          <GlobalStyles />
          <PublicView authenticated={authenticated}>
            <Fragment>{children}</Fragment>
          </PublicView>
        </GlobalStylesLegacy>
      </StaticFrame>
    );
  });

export const AdminAppFrame = ({
  children,
}: {
  children: React.ReactElement;
}): React.ReactElement => {
  const history = useHistory();
  const fetchJson = useFetchJson();
  const [currentUser, setCurrentUser] = useState<User>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const requestAndSetState = async (): Promise<void> => {
      const response = await fetchJson<CurrentUserResponse>(
        ApiPathConfig.getCurrentUser
      );

      const user = new User(response);
      setCurrentUser(user);
      setLoading(false);
    };

    requestAndSetState();
  }, []);

  const Header = () => (
    <a
      className={styles.adminHeader}
      href="#"
      onClick={() => {
        history.push(RoutesConfig.admin.adminTermsOverview.path);
      }}
    >
      <img className={styles.adminLogo} src={newWorkLogo} />
      <Headline className={styles.adminHeaderText} as="h1">
        Terms & Conditions Admin
      </Headline>
    </a>
  );

  if (loading) {
    return null;
  }

  if (!currentUser.hasTermsAndConditionsAdmin) {
    return <p>Permission denied</p>;
  }

  return (
    <Fragment>
      <ReactNotification />

      <GlobalStylesLegacy>
        <GlobalStyles />
        <div className={styles.adminSection}>
          <ContainedGrid className="" type="wide">
            <Header></Header>
            <div className={styles.adminBody}>{children}</div>
          </ContainedGrid>
        </div>
      </GlobalStylesLegacy>
    </Fragment>
  );
};
